/* eslint-disable consistent-return, no-undef, no-nested-ternary, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, prefer-template */

import { DropDown, Modal } from '@gameday/react-components/components'
import Checkbox from '@gameday/react-components/components/Checkbox'
import InputField from '@gameday/react-components/components/InputField'
import LinkifyText from '@gameday/react-components/components/LinkifyText'
import ListItem from '@gameday/react-components/components/ListItem'
import theme, { rem } from '@gameday/react-components/utils/theme'
import { KeyboardArrowDown } from '@material-ui/icons'
import cn from 'classnames'
import { isEmpty, isNil } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'

import { amountDisplay } from '../../../../formats'
import { processProducts } from '../../../../functions/amountProcessing'
import { download } from '../../../../functions/documents'
import { combinationLeft, optionAvailable } from './utils'

const StyledProduct = styled.div`
  width: 100%;
  position: relative;

  & + & {
    margin-top: ${rem(10)};
  }
`

const StyledListItem = styled(ListItem)`
  &.MuiButtonBase-root {
    background-color: ${theme.colors.white};
  }
`

const StyledLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  align-self: stretch;
  flex: 1;
  padding: ${rem(18)} ${rem(18)} ${rem(18)} ${rem(11)};

  ${props =>
    props.hasImage &&
    css`
      flex-direction: row;
      padding: ${rem(12)} ${rem(12)} ${rem(12)} ${rem(12)};
    `}
`

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 2;
  padding-left: ${rem(12)};
`

const StyledImage = styled.div`
  border: 1px solid ${theme.colors.border};
  display: flex;

  ${props =>
    props.inModal &&
    css`
      margin-bottom: ${rem(12)} > img {
        margin: 0 auto;
      }
    `}
`

const StyledSpan = styled.span`
  color: ${theme.colors.teal};
  font-size: ${theme.typography.fontSizeTinyish};
  text-decoration: underline;
  position: relative;
  margin-bottom: ${rem(6)};
  z-index: 21;
`

const StyledChevron = styled(KeyboardArrowDown)`
  cursor: pointer;
  font-size: 1rem !important;
  position: absolute;
  top: 1px;
  ${props =>
    props.isOpen &&
    css`
      transform: rotate(-180deg);
    `}
`

const StyledOptionGroups = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`

const StyledDropDown = styled(DropDown)`
  .bordered .MuiFormControl-root {
    border-radius: 30px;
    margin-top: 3px;

    .MuiSelect-root {
      font-size: ${theme.typography.fontSizeSmaller};
      padding: 0 34px 0 14px;
    }

    .MuiSvgIcon-root {
      right: 4px;
    }
  }

  ${props =>
    props.isError &&
    css`
      .bordered .MuiFormControl-root {
        border-color: ${theme.colors.red};
        .MuiInputBase-root {
          color: ${theme.colors.red} !important;
          .MuiSelect-icon {
            color: ${theme.colors.red} !important;
          }
        }
      }
    `}
`

const StyledInput = styled(InputField)``

const StyledRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-start;
  align-self: stretch;
  text-align: right;
  padding: ${rem(18)} ${rem(14)} ${rem(16)} 0;
  color: ${`var(--gameday-body-textColorLight, ${theme.body.textColorLight})`};
`

const StyledSoldOut = styled.div`
  font-size: ${theme.typography.fontSizeSm};
  font-weight: ${theme.typography.fontWeightBolder};
  color: ${theme.status.errorColor};
`

const StyledPaid = styled.div`
  font-size: ${theme.typography.fontSizeSm};
  font-weight: ${theme.typography.fontWeightBolder};
  color: ${`var(--gameday-body-textColorDark, ${theme.body.textColorDark})`};
`

const StyledCheckbox = styled.div`
  margin: 0 0 ${rem(10)};
  transform: translateX(4px);
`

const StyledPrice = styled.span`
  font-size: ${theme.typography.fontSizeSm};
  font-weight: ${theme.typography.fontWeightBolder};
  color: ${`var(--gameday-body-textColorDark, ${theme.body.textColorDark})`};
  display: inline-block;
  padding-right: ${rem(8)};
`

const StyledItem = styled.div`
  font-size: ${theme.typography.fontSizeTinyish};
  color: ${`var(--gameday-body-textColorLight, ${theme.body.textColorLight})`};
  margin: 0 0 ${rem(4)};

  &.name {
    font-size: ${theme.typography.fontSizeSm};
    font-weight: ${theme.typography.fontWeightBolder};
    color: ${`var(--gameday-body-textColorDark, ${theme.body.textColorDark})`};
    margin: 0 0 ${rem(8)};
  }

  &.organisation,
  &.definedByName {
    font-size: ${theme.typography.fontSizeSmaller};
    margin: 0 0 ${rem(8)};
  }

  &.description {
    font-size: ${theme.typography.fontSizeTinyish};
  }

  &.fees {
    font-size: ${theme.typography.fontSizeTinyish};
    font-style: italic;
  }

  &.belowMinimum {
    color: ${theme.status.errorColor};
  }

  &:last-child {
    margin: 0;
  }
`

export const StyledQty = styled.div`
  margin-top: auto;
`

export const StyledQtyLimit = styled.div`
  width: 100%;
  font-size: ${theme.typography.fontSizeTinyish};
  margin: 0 0 ${rem(2)};

  ${props =>
    props.hasError &&
    props.errorType === 'min' &&
    css`
      .min {
        color: ${theme.colors.red};
      }
    `}

  ${props =>
    props.hasError &&
    props.errorType === 'max' &&
    css`
      .max {
        color: ${theme.colors.red};
      }
    `}
`

export const StyledQtyInput = styled.div`
  display: flex;
  flex-direction: row;
  .content-wrapper {
    padding-bottom: 0 !important;
  }

  .bordered .custom-textfield {
    background-color: transparent !important;
    border: none !important;
    height: ${rem(30)} !important;
    width: ${rem(32)} !important;

    input {
      background-color: ${theme.colors.midGrey};
      border-radius: 50% !important;
      color: ${theme.colors.white};
      text-align: center;
      padding: 0;
    }

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    input[type='number'] {
      -moz-appearance: textfield;
    }
  }

  .label {
    font-weight: ${theme.typography.fontWeightNormal} !important;
    min-height: ${rem(30)} !important;
  }

  ${props =>
    props.checked &&
    css`
      .bordered .custom-textfield input {
        background-color: ${theme.colors.blue};
      }

      ${props =>
        props.isError &&
        css`
          .bordered .custom-textfield input {
            background-color: ${theme.colors.red};
          }
        `}
    `}
`

export const IncrementButton = styled.div`
  background-color: ${theme.colors.lightGrey};
  color: ${theme.colors.lightGrey};
  display: flex;
  font-size: ${theme.typography.fontSizeSm};
  font-weight: bold;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  margin-top: 3px;
  height: 26px;
  width: 30px;

  ${props =>
    props.left &&
    css`
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      margin-right: -15px;
      padding: 0 25px 0 15px;
    `}

  ${props =>
    props.right &&
    css`
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      margin-left: -15px;
      padding: 0 15px 0 25px;
    `}

  ${props =>
    props.checked &&
    css`
      color: ${theme.colors.blue};
    `}
`

const StyledOverlay = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${theme.colors.white};
  z-index: 20;
  border-radius: 4px;
  cursor: default;
  opacity: 0.4;
`

const Product = ({
  ageGroup,
  price,
  competition,
  currencyCode,
  definedById,
  definedByName,
  errorType,
  id,
  sfid,
  name,
  organisation,
  description,
  season,
  minimumPurchaseLimit,
  purchaseLimit,
  isMandatory,
  value = {
    quantity: 0,
    checked: false,
  },
  formType,
  onChange = () => {},
  identifier,
  error = {},
  errorText = '',
  // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
  dependentProducts = [],
  // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
  values,
  paidProducts = {},
  childProducts = {},
  preselectedProduct,
  feeConfigurations,
  remainingProductQuantity,
  allowMultiplePurchase,
  paymentGateway,
  type,
  imageURL,
  deliveryMethod,
  productAttributes,
  combinations = [],
  formData,
  displayPriceAsZero = false,
}) => {
  const [productImage, setProductImage] = useState('')
  const [openModal, setOpenModal] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedAttr, setSelectedAttr] = useState(null)
  const [limit, setQuantityLimit] = useState(null)
  const minimum = allowMultiplePurchase ? minimumPurchaseLimit || 1 : 1
  const productBelow = need => remainingProductQuantity != null && type !== 'Team' && remainingProductQuantity < need
  const isSoldOut = productBelow(minimum)
  const severalAttributes = (productAttributes || []).length > 1
  const chosenOptionIds = value.optionIds || []
  const isOptionAvailable = (groupIndex, optionId, chosen = chosenOptionIds, need = minimum) =>
    optionAvailable({
      productAttributes,
      combinations,
      productLeft: remainingProductQuantity,
      chosen,
      groupIndex,
      optionId,
      minimum: need,
    })
  const optionBelow = (option, need) =>
    option.remainingProductQuantity != null && option.remainingProductQuantity < need
  const attributesSoldOutBelow = need =>
    severalAttributes
      ? (productAttributes[0].items || []).every(item => !isOptionAvailable(0, item.id, [], need))
      : !isEmpty(productAttributes) &&
        productAttributes.every(group => group.items?.length > 0 && group.items.every(item => optionBelow(item, need)))
  const allAttributesSoldOut = attributesSoldOutBelow(minimum)
  const fewerLeftThanMinimum =
    minimum > 1 && (isSoldOut || allAttributesSoldOut) && !productBelow(1) && !attributesSoldOutBelow(1)
  // biome-ignore lint/complexity/useOptionalChain: TODO
  const isPaid = paidProducts[identifier] && paidProducts[identifier].isPaid
  const isPaidNotMultiple = isPaid && allowMultiplePurchase === false

  const [processingAndTransactionFee, setFee] = useState(null)
  const item = useRef(null)

  useEffect(() => {
    const processedProductsData = processProducts(
      formType === 'Event' ? 'Event Form' : 'Membership Form',
      [
        {
          organisation: definedById,
          id,
          name,
          listPrice: price,
          totalPrice: price,
          quantity: 1,
        },
      ],
      feeConfigurations,
      !isEmpty(paymentGateway) ? paymentGateway[0] : null
    )

    const { processedProducts } = processedProductsData
    setFee(processedProducts[0].surcharge)
  }, [])

  useEffect(() => {
    if (imageURL) {
      download({
        file: imageURL,
        info: {
          loggedInUser: formData.userId || 'not specified',
          objectId: formData.profileId,
          objectName: 'productImage',
          component: formData.pageName || 'Image Download',
          formId: formData.form,
          action: 'download',
          target: 'Product Image',
          funcName: 'ProductImageDownload',
          application: formData.application,
        },
        url: formData.downloadEndpoint,
      }).then(response => {
        setProductImage(response)
      })
    }
  }, [imageURL])

  useEffect(() => {
    let setLimit = purchaseLimit
    let setAttrLimit
    if (selectedAttr) {
      const { quantityLimit, remainingProductQuantity } = selectedAttr
      remainingProductQuantity < quantityLimit
        ? // biome-ignore lint/suspicious/noAssignInExpressions: TODO
          (setAttrLimit = remainingProductQuantity)
        : // biome-ignore lint/suspicious/noAssignInExpressions: TODO
          (setAttrLimit = quantityLimit)
      purchaseLimit
        ? setAttrLimit < purchaseLimit
          ? // biome-ignore lint/suspicious/noAssignInExpressions: TODO
            (setLimit = setAttrLimit)
          : // biome-ignore lint/suspicious/noAssignInExpressions: TODO
            (setLimit = purchaseLimit)
        : // biome-ignore lint/suspicious/noAssignInExpressions: TODO
          (setLimit = setAttrLimit)
    }
    // Clamp by product-level remaining stock
    if (remainingProductQuantity != null && type !== 'Team') {
      setLimit = setLimit != null ? Math.min(setLimit, remainingProductQuantity) : remainingProductQuantity
    }
    setQuantityLimit(setLimit)
    if (!allAttributesSoldOut && value.checked && !isSoldOut) {
      onChange(identifier, {
        ...value,
      })
    }
  }, [purchaseLimit, selectedAttr, remainingProductQuantity])

  const hasError = !!error.quantity
  const enableMandatoryOverlay = isMandatory && !allowMultiplePurchase && isEmpty(productAttributes)
  const hasImage = imageURL && type === 'Add-on'

  const renderOptionGroups = () => (
    <StyledOptionGroups>
      {productAttributes.map((group, groupIndex) => (
        <StyledDropDown
          key={group.productAttributeId}
          border
          label={group.productAttributeName}
          defaultOption="Please Select"
          filterData={[
            {
              key: `attributes-${group.productAttributeId}`,
              value: (group.items || []).map(item => {
                const available = isOptionAvailable(groupIndex, item.id)
                return {
                  id: item.id,
                  name: available ? item.name : `${item.name}  SOLD OUT`,
                  disabled: !available,
                }
              }),
              key_prefix: '',
            },
          ]}
          onChange={e => {
            const picked = e === '' ? null : e
            const optionIds = productAttributes.map((_, index) =>
              index === groupIndex ? picked : chosenOptionIds[index] != null ? chosenOptionIds[index] : null
            )
            onChange(identifier, { ...value, attributeId: undefined, optionIds })
            const complete = optionIds.every(id => id != null)
            const left = complete
              ? combinationLeft(optionIds, combinations, remainingProductQuantity, productAttributes)
              : null
            setSelectedAttr(left == null ? null : { quantityLimit: left, remainingProductQuantity: left })
          }}
          value={value}
          isRequired
          required
          noSpacing
          isError={value.checked && chosenOptionIds[groupIndex] == null}
          disabled={!value.checked}
          selected={chosenOptionIds[groupIndex] != null ? chosenOptionIds[groupIndex] : ''}
          data-testid="productAttributeDropdown"
        />
      ))}
    </StyledOptionGroups>
  )

  const renderProdAttrs = () => {
    if (severalAttributes) return renderOptionGroups()
    // biome-ignore lint/complexity/useOptionalChain: TODO
    const prodAttrs = (productAttributes && productAttributes[0]) || {}
    const { isMandatory: isMand = true, items = [], productAttributeName = '' } = prodAttrs
    const isQualificationStandard = productAttributeName.toLowerCase() === 'qualification standard'
    return !isQualificationStandard ? (
      <StyledDropDown
        border
        defaultOption="Please Select"
        filterData={[
          {
            key: 'attributes',
            value:
              items.map(i => ({
                id: i.id,
                // biome-ignore lint/style/useTemplate: TODO
                name: optionBelow(i, minimum) ? i.name + '  SOLD OUT' : i.name,
                disabled: optionBelow(i, minimum),
              })) || [],
            key_prefix: '',
          },
        ]}
        onChange={e => {
          onChange(identifier, {
            ...value,
            attributeId: e,
          })
          const attr = items.filter(i => i.id === e)
          setSelectedAttr(attr[0])
        }}
        value={value}
        isRequired
        required
        noSpacing
        isError={value.checked && !value.attributeId}
        disabled={!value.checked}
        selected={value.attributeId}
        data-testid="productAttributeDropdown"
      />
    ) : (
      <StyledInput
        value={value.attributeValue || ''}
        label={items[0].name}
        withBorder
        withDivider={false}
        disabled={!value.checked}
        isError={isMand && ((value.checked && !value.attributeValue) || (value.checked && errorText))}
        errorText={errorText}
        noWrap
        noMobile
        dataTestId="qualificationStandardField"
        onChange={e => {
          /* eslint-disable-next-line prefer-const */
          const val = e.target.value
          onChange(identifier, {
            ...value,
            attributeValue: val,
          })
        }}
      />
    )
  }

  const onCheckedChange = val => {
    if (isMandatory) {
      return
    }
    const purchase = {
      checked: val,
      quantity: val ? (allowMultiplePurchase ? minimumPurchaseLimit || '1' : '1') : undefined,
      ...((type === 'Add-on' || (type === 'event' && subType === 'Event Entry')) &&
        productAttributes && { attributeId: undefined, attributeValue: null, optionIds: undefined }),
    }
    if (Object.keys(childProducts).length < 1) {
      return onChange(identifier, purchase)
    }

    // clear all child products
    if (!val && !isPaid) {
      Object.keys(childProducts).forEach(cp => {
        onChange(cp, {
          checked: false,
          quantity: undefined,
        })
      })
    }

    // check preselected product
    if (
      val &&
      preselectedProduct &&
      Object.keys(childProducts).includes(preselectedProduct) &&
      (isPaid || !childProducts[preselectedProduct].mustBePaid)
    ) {
      onChange(preselectedProduct, {
        checked: true,
        quantity: '1',
      })
    }
    // check child mandatory product
    if (val) {
      Object.keys(childProducts).forEach(cp => {
        const childP = childProducts[cp]
        if (childP.isMandatory && (isPaid || !childP.mustBePaid) && childP.allowMultiplePurchase) {
          onChange(cp, {
            checked: true,
            quantity: 1,
          })
        }
      })
    }

    onChange(identifier, purchase)
  }

  return (
    <StyledProduct>
      <StyledListItem
        forwardRef={item}
        type="checkbox"
        data-testid="productContainer"
        onSelected={() => {
          onCheckedChange(!value.checked)
        }}
        OverrideComponent={
          isSoldOut || isPaidNotMultiple || allAttributesSoldOut ? (
            <StyledRight>
              {isSoldOut ||
              (allowMultiplePurchase && minimumPurchaseLimit > remainingProductQuantity) ||
              allAttributesSoldOut ? (
                <StyledSoldOut>Sold out</StyledSoldOut>
              ) : (
                <StyledPaid>PAID</StyledPaid>
              )}
            </StyledRight>
          ) : (
            <StyledRight>
              <StyledCheckbox>
                <Checkbox
                  id={`product-${id}-checkbox`}
                  data-testid="productCheckbox"
                  label={
                    !isNil(price) && (
                      <StyledPrice>
                        {price === 0
                          ? displayPriceAsZero
                            ? amountDisplay('0.00', currencyCode)
                            : 'FREE'
                          : amountDisplay(price, currencyCode)}
                      </StyledPrice>
                    )
                  }
                  labelPlacement={cn('left')}
                  isChecked={value.checked}
                  noPadding
                  onChecked={val => onCheckedChange(val)}
                  disabled={isMandatory}
                  inputProps={isMandatory ? { style: { pointerEvents: 'none' } } : undefined}
                  labelIsTrigger
                />
              </StyledCheckbox>
              {(formType === 'event' || formType === 'shop' || formType === 'membership') && allowMultiplePurchase ? (
                <StyledQty>
                  {(purchaseLimit || minimumPurchaseLimit) && (
                    <StyledQtyLimit hasError={hasError} errorType={errorType}>
                      {minimumPurchaseLimit > 0 && <span className="min">Min: {minimumPurchaseLimit}</span>}
                      {minimumPurchaseLimit > 0 && purchaseLimit && ' | '}
                      {purchaseLimit && <span className="max">Max: {limit != null ? limit : purchaseLimit}</span>}
                    </StyledQtyLimit>
                  )}
                  {!isNil(purchaseLimit) && (
                    <StyledQtyInput checked={value.checked} isError={hasError} onClick={e => e.stopPropagation()}>
                      <IncrementButton
                        left
                        onClick={event => {
                          event.stopPropagation()
                          if (minimumPurchaseLimit > 0 ? value.quantity > minimumPurchaseLimit : value.quantity > 1) {
                            onChange(identifier, {
                              ...value,
                              quantity: value.quantity - 1,
                            })
                          }
                        }}
                        checked={value.checked}
                      >
                        -
                      </IncrementButton>
                      <InputField
                        id={`product-${id}`}
                        key={id}
                        value={value.quantity || ''}
                        type="number"
                        min={1}
                        max={limit != null ? limit : purchaseLimit}
                        customMargin="0"
                        name={sfid}
                        isLabelInline
                        withBorder
                        withDivider={false}
                        disabled={!value.checked}
                        isError={hasError}
                        errorText={hasError && ' '}
                        noWrap
                        noMobile
                        onChange={e => {
                          e.stopPropagation()
                          let val = e.target.value
                          if (val && Number(val) < 1) {
                            val = '1'
                          }
                          if (limit && val && Number(val) > limit) {
                            val = limit
                          }
                          onChange(identifier, {
                            ...value,
                            quantity: val,
                          })
                        }}
                      />
                      <IncrementButton
                        right
                        onClick={event => {
                          event.stopPropagation()
                          if (limit && value.quantity < limit) {
                            onChange(identifier, {
                              ...value,
                              quantity: Number(value.quantity) + 1,
                            })
                          }
                        }}
                        checked={value.checked}
                      >
                        +
                      </IncrementButton>
                    </StyledQtyInput>
                  )}
                </StyledQty>
              ) : null}
            </StyledRight>
          )
        }
        isChecked={value.checked}
        disabled={isSoldOut || isPaidNotMultiple || allAttributesSoldOut}
      >
        <StyledLeft hasImage={hasImage}>
          {hasImage && (
            <StyledImage
              onClick={event => {
                event.stopPropagation()
                setOpenModal(true)
              }}
            >
              <img src={productImage} width="80px" alt={name} />
            </StyledImage>
          )}
          <StyledContent>
            <StyledItem className="name">{name && name}</StyledItem>
            {!isNil(organisation) && <StyledItem className="organisation">{organisation.name}</StyledItem>}
            {!isNil(definedByName) && <StyledItem className="definedByName">{definedByName}</StyledItem>}
            {!isNil(description) && description !== '' && (
              <>
                <StyledSpan
                  onClick={event => {
                    event.stopPropagation()
                    setIsOpen(!isOpen)
                  }}
                >
                  Description
                  <StyledChevron isOpen={isOpen} />
                </StyledSpan>
                {isOpen && (
                  <StyledItem className="description">
                    <LinkifyText>{description}</LinkifyText>
                  </StyledItem>
                )}
              </>
            )}
            {fewerLeftThanMinimum && (
              <StyledItem className="belowMinimum" data-testid="productBelowMinimum">
                Fewer are left than the minimum of {minimum} per purchase.
                {!isNil(organisation) && ` Contact ${organisation.name} to buy this.`}
              </StyledItem>
            )}
            {!isNil(competition) && <StyledItem className="competition">{competition}</StyledItem>}
            {!isNil(deliveryMethod) && (
              <StyledItem className="deliveryMethod">
                <strong>Delivery Method:</strong> {deliveryMethod}
              </StyledItem>
            )}
            {!isNil(season) && <StyledItem className="season">{season}</StyledItem>}
            {!isNil(ageGroup) && <StyledItem className="ageGroup">{ageGroup}</StyledItem>}
            {!isNil(processingAndTransactionFee) &&
              processingAndTransactionFee !== 0 &&
              !isPaidNotMultiple &&
              !isSoldOut && (
                <StyledItem className="fees">
                  Processing Fee {amountDisplay(processingAndTransactionFee, currencyCode)}
                </StyledItem>
              )}
            {/** biome-ignore lint/a11y/useKeyWithClickEvents: TODO */}
            {/** biome-ignore lint/a11y/noStaticElementInteractions: TODO */}
            <div onClick={e => e.stopPropagation()}>{!isEmpty(productAttributes) && renderProdAttrs()}</div>
          </StyledContent>
        </StyledLeft>
      </StyledListItem>
      {enableMandatoryOverlay && <StyledOverlay />}

      {hasImage && (
        <Modal isOpen={openModal} handleModalClose={() => setOpenModal(false)}>
          <StyledImage inModal>
            <img src={productImage} width="500px" alt={name} />
          </StyledImage>
        </Modal>
      )}
    </StyledProduct>
  )
}

export default Product
